import { Avatar, Chip } from '@mui/material';
import {
  AutocompleteInput,
  Datagrid,
  FunctionField,
  List,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  useRecordContext,
  type RaRecord,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { formatRate } from './formatRate';
import { StoreBulkActions } from './StoreBulkActions';

/**
 * Stores list — the first Phase 1 surface (read-only).
 *
 * A store is modelled as merchants -> merchant_regions (per market) -> channels.
 * The cashback rate, affiliate network and active flag live on the PER-MARKET
 * merchant_regions row, so the list shows merchants joined to their region row
 * FOR THE CURRENTLY ACTIVE REGION (the one the 0.5 switcher sets). The join is
 * done once, server-side, by the provider's read-only `stores` resource — the
 * surface never touches the seed and issues a single getList.
 *
 * Region behaviour: the active region flows through the provider's ONE
 * access-scope -> market mapping via the permanent `region` filter (read from
 * useRegion). Flip the switcher and the rows, rates and statuses change to that
 * market; a single-region user only ever sees theirs.
 */

const StoreLogo = () => {
  const record = useRecordContext();
  const name = (record?.name as string) ?? '';
  const url = (record?.logoUrl as string | null) ?? null;
  // Small thumbnail; falls back to the brand's initial when logoUrl is null.
  return (
    <Avatar
      src={url ?? undefined}
      alt={name}
      variant="rounded"
      sx={{ width: 28, height: 28, fontSize: 14, bgcolor: 'action.selected' }}
    >
      {name.charAt(0).toUpperCase() || '?'}
    </Avatar>
  );
};

const StatusChip = () => {
  const record = useRecordContext();
  const active = Boolean(record?.active);
  return (
    <Chip
      size="small"
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
    />
  );
};

// Category / network / status filters. Region is driven by the switcher, NOT a
// column filter. `q` is a case-insensitive substring search (matches name).
const storeFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
  <ReferenceInput
    key="categoryId"
    source="categoryId"
    reference="categories"
    sort={{ field: 'name', order: 'ASC' }}
    perPage={100}
  >
    <AutocompleteInput label="Category" optionText="name" />
  </ReferenceInput>,
  <ReferenceInput
    key="networkId"
    source="networkId"
    reference="affiliateNetworks"
    sort={{ field: 'name', order: 'ASC' }}
    perPage={100}
  >
    <AutocompleteInput label="Network" optionText="name" />
  </ReferenceInput>,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'active', name: 'Active' },
      { id: 'inactive', name: 'Inactive' },
    ]}
  />,
];

/** The list itself. Exported for testing without the access wrapper. */
export const StoreListView = () => {
  const { activeRegion } = useRegion();
  return (
    <List
      resource="stores"
      title="Stores"
      filter={{ region: activeRegion }}
      filters={storeFilters}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      {/* Clicking a row opens the store detail, keyed by the BRAND (merchantId),
          not this per-market list row's id. Bulk actions (Phase 8, the first in
          the build) operate at the per-MARKET grain on the selected rows and gate
          on stores:write inside the component, so a viewer sees none
          (D-multi-channel-editor). */}
      <Datagrid
        rowClick={(_id, _resource, record) =>
          `/stores/${String(record.merchantId)}/show`
        }
        bulkActionButtons={<StoreBulkActions />}
      >
        <FunctionField
          label="Logo"
          sortable={false}
          render={() => <StoreLogo />}
        />
        <TextField source="name" label="Name" />
        <TextField source="category" label="Category" />
        <FunctionField
          label="Cashback rate"
          sortBy="baseRate"
          render={(record: RaRecord) => formatRate(record.baseRate)}
        />
        <FunctionField
          label="Network"
          sortBy="network"
          render={(record: RaRecord) =>
            (record.network as string | null) ?? '—'
          }
        />
        <FunctionField
          label="Status"
          sortBy="status"
          render={() => <StatusChip />}
        />
      </Datagrid>
    </List>
  );
};

/**
 * Gated entry point registered as the `stores` Resource's list. Read-only, so
 * `viewer` (read everywhere) sees it alongside `super_admin`; editor/support/
 * fraud do not have `stores:read` and never see the route or the menu item.
 */
export const StoreList = () => (
  <RequireAccess capability="stores:read">
    <StoreListView />
  </RequireAccess>
);
